import { Box, Button, Icon, SxProps } from "@outschool/backpack";
import { Activity, PublicReview } from "@outschool/gql-backend-generated";
import { faEllipsisH } from "@outschool/icons";
import React from "react";
import { Manager, Popper, Reference } from "react-popper";

import { EditReviewButton } from "./EditReviewButton";

type MoreOptionsButtonProps = {
  activity: Activity;
  review: PublicReview;
};

export const MoreOptionsButton: React.FunctionComponent<
  MoreOptionsButtonProps
> = ({ activity, review }) => {
  const [shouldShowMoreOptions, setShouldShowMoreOptions] =
    React.useState<boolean>(false);

  return (
    <Manager>
      <Reference>
        {({ ref }) => (
          <Button
            variant="link"
            onClick={() => setShouldShowMoreOptions(!shouldShowMoreOptions)}
            ref={ref}
            sx={{
              color: "#8791A0",
              marginLeft: "10px",
            }}
          >
            <Icon icon={faEllipsisH} />
          </Button>
        )}
      </Reference>

      {shouldShowMoreOptions && (
        <Popper
          placement="bottom-end"
          modifiers={{
            preventOverflow: {
              boundariesElement: "viewport",
            },
          }}
        >
          {({ ref, style }) => (
            <MoreOptionsMenu
              ref={ref}
              // @ts-ignore style from Popper not using the same CSS typedefs as we are
              style={style}
              activity={activity}
              review={review}
              hideMoreOptions={() => setShouldShowMoreOptions(false)}
            />
          )}
        </Popper>
      )}
    </Manager>
  );
};

type MoreOptionsMenuProps = {
  style: SxProps;
  activity: MoreOptionsButtonProps["activity"];
  review: MoreOptionsButtonProps["review"];
  hideMoreOptions: () => void;
};

const MoreOptionsMenu = React.forwardRef(
  ({ style, activity, review, hideMoreOptions }: MoreOptionsMenuProps, ref) => {
    /* close menu when user clicks outside of it */
    const wrapperRef = React.useRef(null);

    const useCloseOnOutsideClick = (ref: React.RefObject<any>) => {
      React.useEffect(() => {
        function handleOutsideClick(event: MouseEvent) {
          if (ref.current && !ref.current.contains(event.target)) {
            hideMoreOptions();
          }
        }
        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
          document.removeEventListener("mousedown", handleOutsideClick);
        };
      }, [ref]);
    };

    useCloseOnOutsideClick(wrapperRef);

    return (
      <Box ref={wrapperRef}>
        <Box
          ref={ref}
          sx={[
            {
              borderRadius: "16px",
              boxShadow: "0px 4px 24px rgba(0, 0, 0, 0.12)",
              minWidth: "150px",
              zIndex: 1000,
              backgroundColor: "common.white",
            },
            ...(Array.isArray(style) ? style : [style]),
          ]}
        >
          <EditReviewButton activity={activity} review={review} />
        </Box>
      </Box>
    );
  }
);
